import { Injectable, Injector, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { AUDIT_SEED } from '@common/data';
import type { AuditEntry, AuditPage, AuditQuery } from '@common/types';

import { CurrentUserService } from './current-user.service';
import { OrganizationService } from './organization.service';

const LIST_LATENCY_MS = 260;

@Injectable({ providedIn: 'root' })
export class AuditService {
  private readonly injector = inject(Injector);
  private readonly entries = signal<ReadonlyArray<AuditEntry>>(AUDIT_SEED);
  private seq = 0;

  readonly all = this.entries.asReadonly();

  list(query: AuditQuery): Observable<AuditPage> {
    let result = this.entries().slice();

    const term = query.search.trim().toLowerCase();
    if (term) {
      result = result.filter(
        (entry) =>
          entry.actorName.toLowerCase().includes(term) ||
          entry.entityLabel.toLowerCase().includes(term) ||
          entry.entityType.toLowerCase().includes(term) ||
          entry.summary.toLowerCase().includes(term),
      );
    }

    const actors = query.filters['actorUserId'];
    if (actors?.length) {
      result = result.filter((entry) => !!entry.actorUserId && actors.includes(entry.actorUserId));
    }

    const actions = query.filters['action'];
    if (actions?.length) result = result.filter((entry) => actions.includes(entry.action));

    const sources = query.filters['source'];
    if (sources?.length) result = result.filter((entry) => sources.includes(entry.source));

    const entityTypes = query.filters['entityType'];
    if (entityTypes?.length) {
      result = result.filter((entry) => entityTypes.includes(entry.entityType));
    }

    const field = query.sortField ?? 'atIso';
    const direction = query.sortOrder === 'asc' ? 1 : -1;
    result.sort((a, b) => {
      const av = String(a[field as keyof AuditEntry] ?? '');
      const bv = String(b[field as keyof AuditEntry] ?? '');
      if (av < bv) return -1 * direction;
      if (av > bv) return 1 * direction;
      return 0;
    });

    const total = result.length;
    const start = Math.max(0, query.first);
    return of({ data: result.slice(start, start + Math.max(1, query.rows)), total }).pipe(
      delay(LIST_LATENCY_MS),
    );
  }

  /**
   * Records a change the signed-in user made to their own settings. Resolved lazily because the
   * services that call this sit underneath `CurrentUserService`'s own dependencies.
   */
  recordOwn(summary: string): void {
    const user = this.injector.get(CurrentUserService);
    const entry: AuditEntry = {
      id: `audit-own-${Date.now().toString(36)}-${++this.seq}`,
      atIso: new Date().toISOString(),
      actorName: user.displayName() || 'You',
      actorEmail: user.email(),
      actorUserId: user.id(),
      action: 'updated',
      entityType: 'User settings',
      entityLabel: user.displayName() || user.email(),
      summary,
      source: 'ui',
      organizationId: this.injector.get(OrganizationService).activeId() ?? '',
      accountId: null,
    };
    this.entries.update((list) => [entry, ...list]);
  }

  /** Distinct entity types, so the filter group reflects what is actually in the log. */
  entityTypes(): ReadonlyArray<string> {
    return [...new Set(this.entries().map((entry) => entry.entityType))].sort();
  }
}
